import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import { ChevronUp, ChevronDown, Download, Share2, ArrowLeft, Loader2, TrendingUp, Eye, MousePointer, DollarSign, PlayCircle, CheckCircle, Sparkles, Pencil, Check, X, Sun, Moon, FileSpreadsheet, Calendar } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agentic-curation/artifacts/zri9wsj7_ingenium-logo.png";

// --- Theme definitions ---
const themes = {
  dark: {
    bg: "#191b1d", card: "#1e2124", border: "#2a2d31", text: "#ebebeb",
    textMuted: "rgba(235,235,235,0.6)", textFaint: "rgba(235,235,235,0.4)",
    accent: "#ff3737", gridStroke: "#2a2d31", hoverRow: "#252830",
    tooltipBg: "#1e2124", tooltipBorder: "#2a2d31", tooltipText: "#ffffff",
  },
  light: {
    bg: "#f5f5f7", card: "#ffffff", border: "#e2e2e5", text: "#1a1a1a",
    textMuted: "rgba(0,0,0,0.55)", textFaint: "rgba(0,0,0,0.35)",
    accent: "#ff3737", gridStroke: "#e2e2e5", hoverRow: "#f0f0f2",
    tooltipBg: "#ffffff", tooltipBorder: "#e2e2e5", tooltipText: "#1a1a1a",
  },
};

// --- Formatters ---
const fmtWhole = (n) => { if (n == null) return "-"; return Math.round(Number(n)).toLocaleString("en-US"); };
const fmtShort = (n) => { if (n == null) return "-"; if (n >= 1e6) return (n / 1e6).toFixed(1) + "M"; if (n >= 1e3) return (n / 1e3).toFixed(1) + "K"; return Number(n).toFixed(0); };
const fmtDec = (n, dec = 2) => { if (n == null) return "-"; return Number(n).toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec }); };
const pct = (n) => (n == null ? "-" : Number(n).toFixed(2) + "%");
const cur = (n, cy = "HKD") => (n == null ? "-" : `${cy} ${fmtDec(n, 2)}`);
const fmtCpcv = (n) => { if (n == null || n === 0) return "-"; return Number(n).toFixed(3); };

const METRIC_KEYS = ['impressions','clicks','ctr','cpc','spend','vtr','cpcv','video_starts','video_25','video_50','video_75','video_100','cpm_source','cpc_source'];
const getDimKey = (rows) => { if (!rows?.length) return null; return Object.keys(rows[0]).find(k => !METRIC_KEYS.includes(k)); };

// --- Delete button for sections ---
const DeleteBtn = ({ onClick, theme }) => (
  <button onClick={onClick} className="absolute top-2 right-2 p-1 rounded hover:bg-red-500/20 transition-colors z-10" style={{ color: theme.textFaint }} title="Remove section" data-testid="section-delete-btn">
    <X className="w-3.5 h-3.5" />
  </button>
);

const Section = ({ id, hidden, onHide, isShared, theme, children }) => {
  if (hidden) return null;
  return (
    <div className="relative group/section">
      {!isShared && <div className="opacity-0 group-hover/section:opacity-100 transition-opacity"><DeleteBtn onClick={() => onHide(id)} theme={theme} /></div>}
      {children}
    </div>
  );
};

// --- CSV Export Button ---
const CsvExportBtn = ({ campaignId, section, token, theme }) => {
  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/export-csv?section=${section}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${section}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };
  return (
    <button onClick={handleExport} className="p-1.5 rounded transition-colors" style={{ color: theme.textFaint }} title="Export as CSV" data-testid={`csv-export-${section}`}>
      <FileSpreadsheet className="w-3.5 h-3.5" />
    </button>
  );
};

// --- Sortable Table ---
const SortableTable = ({ data, columns, greenMetric, title, campaignId, section, token, theme }) => {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };
  const sorted = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => (sortDir === "asc" ? (a[sortKey] ?? 0) - (b[sortKey] ?? 0) : (b[sortKey] ?? 0) - (a[sortKey] ?? 0)));
  }, [data, sortKey, sortDir]);
  if (!data?.length) return null;
  const bestVals = {};
  if (greenMetric) {
    (Array.isArray(greenMetric) ? greenMetric : [greenMetric]).forEach(m => {
      if (m === 'cpc' || m === 'cpcv') {
        const vals = data.filter(r => r[m] > 0).map(r => r[m]);
        bestVals[m] = vals.length ? Math.min(...vals) : null;
      } else bestVals[m] = Math.max(...data.map(r => r[m] || 0));
    });
  }
  return (
    <div className="rounded-lg border overflow-hidden" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
      {title && (
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: theme.border }}>
          <h3 className="text-sm font-semibold" style={{ color: theme.text }}>{title}</h3>
          {campaignId && section && <CsvExportBtn campaignId={campaignId} section={section} token={token} theme={theme} />}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b" style={{ borderColor: theme.border }}>{columns.map(col => (
            <th key={col.key} onClick={() => col.sortable !== false && toggleSort(col.key)}
              className={`px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider ${col.sortable !== false ? 'cursor-pointer' : ''} ${col.align === 'right' ? 'text-right' : ''}`}
              style={{ color: theme.textMuted }}>
              <span className="inline-flex items-center gap-1">{col.label}{sortKey === col.key && (sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}</span>
            </th>))}</tr></thead>
          <tbody>{sorted.map((row, i) => (
            <tr key={i} className="border-b transition-colors" style={{ borderColor: `${theme.border}80` }} onMouseEnter={e => e.currentTarget.style.backgroundColor = theme.hoverRow} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>{columns.map(col => {
              const val = row[col.key];
              const isGreen = bestVals[col.key] != null && val === bestVals[col.key];
              return <td key={col.key} className={`px-4 py-2.5 text-xs ${col.align === 'right' ? 'text-right' : ''} ${isGreen ? 'font-semibold' : ''}`} style={{ color: isGreen ? '#22c55e' : theme.text }}>{col.render ? col.render(val, row) : (val ?? "-")}</td>;
            })}</tr>))}</tbody>
        </table>
      </div>
    </div>
  );
};

// --- KPI Card ---
const KpiCard = ({ icon: Icon, label, value, theme }) => (
  <div className="rounded-lg border p-4" style={{ backgroundColor: theme.card, borderColor: theme.border }} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
    <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4" style={{ color: theme.accent }} /><span className="text-xs uppercase tracking-wider" style={{ color: theme.textMuted }}>{label}</span></div>
    <div className="text-xl font-bold" style={{ color: theme.text }}>{value}</div>
  </div>
);

// --- Video Funnel ---
const VideoFunnel = ({ funnel, theme }) => {
  if (!funnel) return null;
  const stages = [
    { label: "Video Starts", value: funnel.starts, color: "#ff3737" },
    { label: "25% Plays", value: funnel.q1, color: "#d63384" },
    { label: "50% Plays", value: funnel.q2, color: "#9b59b6" },
    { label: "75% Plays", value: funnel.q3, color: "#7c3aed" },
    { label: "100% Plays", value: funnel.complete, color: "#583aff" },
  ];
  const maxVal = funnel.starts || 1;
  return (
    <div className="rounded-lg border p-5" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
      <h3 className="text-sm font-semibold mb-4" style={{ color: theme.text }}>Video Completion Funnel</h3>
      <div className="space-y-3">{stages.map(s => {
        const p = ((s.value / maxVal) * 100).toFixed(1);
        return (<div key={s.label}>
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs" style={{ color: theme.textMuted }}>{s.label}</span>
            <span className="text-xs font-semibold" style={{ color: theme.text }}>{fmtWhole(s.value)} ({p}%)</span>
          </div>
          <div className="w-full rounded-full h-5 overflow-hidden" style={{ backgroundColor: theme.border }}>
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${p}%`, backgroundColor: s.color }} />
          </div>
        </div>);
      })}</div>
    </div>
  );
};

// --- Breakdown Bar Chart ---
const BreakdownChart = ({ data, title, metric = "impressions", metricLabel = "Impressions", theme }) => {
  if (!data || data.length < 2) return null;
  const dimKey = getDimKey(data);
  if (!dimKey) return null;
  const chartData = data.slice().sort((a, b) => (b[metric] || 0) - (a[metric] || 0)).slice(0, 12).map(r => ({
    name: String(r[dimKey]).length > 20 ? String(r[dimKey]).substring(0, 18) + "..." : String(r[dimKey]),
    value: r[metric] || 0,
  }));
  const isPct = metric === "ctr" || metric === "vtr";
  return (
    <div className="rounded-lg border p-5" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
      <h3 className="text-sm font-semibold mb-4" style={{ color: theme.text }}>{title}</h3>
      <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 32)}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.gridStroke} horizontal={false} />
          <XAxis type="number" tick={{ fill: theme.textMuted, fontSize: 10 }} tickFormatter={v => isPct ? v.toFixed(1) + "%" : fmtShort(v)} />
          <YAxis type="category" dataKey="name" tick={{ fill: theme.textMuted, fontSize: 10 }} width={120} />
          <Tooltip contentStyle={{ background: theme.tooltipBg, border: `1px solid ${theme.tooltipBorder}`, borderRadius: 8, fontSize: 11, color: theme.tooltipText }} formatter={v => [isPct ? pct(v) : fmtWhole(v), metricLabel]} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} fill={theme.accent} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// --- Inline Editable Field ---
const EditableField = ({ value, onSave, label, type = "text", theme }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => setVal(value), [value]);
  const save = () => { onSave(val); setEditing(false); };
  const cancel = () => { setVal(value); setEditing(false); };
  if (!editing) return (
    <span className="inline-flex items-center gap-1.5 group">
      {val}
      <button onClick={() => setEditing(true)} className="opacity-0 group-hover:opacity-100 transition-all" style={{ color: theme.textFaint }} data-testid={`edit-${label}-btn`}><Pencil className="w-3 h-3" /></button>
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1">
      <input type={type} value={val} onChange={e => setVal(e.target.value)} className="text-xs px-2 py-0.5 rounded border" style={{ backgroundColor: theme.border, color: theme.text, borderColor: `${theme.accent}40`, width: type === "number" ? 80 : 200 }} autoFocus data-testid={`edit-${label}-input`} onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }} />
      <button onClick={save} className="text-green-400 hover:text-green-300" data-testid={`save-${label}-btn`}><Check className="w-3.5 h-3.5" /></button>
      <button onClick={cancel} style={{ color: theme.textFaint }}><X className="w-3.5 h-3.5" /></button>
    </span>
  );
};

// ============================
// Main Component
// ============================
export default function CampaignDashboard() {
  const { campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const shareToken = searchParams.get("share");
  const navigate = useNavigate();
  const reportRef = useRef(null);

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [hiddenSections, setHiddenSections] = useState([]);
  const [editingInsights, setEditingInsights] = useState(false);
  const [insightsText, setInsightsText] = useState("");
  const [themeMode, setThemeMode] = useState("dark");

  const theme = themes[themeMode];
  const isShared = !!shareToken;
  const token = !isShared ? (localStorage.getItem("adminToken") || localStorage.getItem("clientToken")) : null;
  const isSuperAdmin = localStorage.getItem("adminRole") === "super_admin";

  useEffect(() => { loadCampaign(); }, [campaignId, shareToken]);

  const loadCampaign = async () => {
    setLoading(true);
    try {
      let url, headers = {};
      if (shareToken) { url = `${API}/campaigns/shared/${shareToken}`; }
      else {
        if (!token) { navigate("/?login=true"); return; }
        url = `${API}/campaigns/${campaignId}`;
        headers = { Authorization: `Bearer ${token}` };
      }
      const res = await axios.get(url, { headers });
      setCampaign(res.data);
      setHiddenSections(res.data.hidden_sections || []);
      setInsightsText(res.data.insights || "");
      if (res.data.share_token) setShareUrl(`${window.location.origin}/campaigns/view?share=${res.data.share_token}`);
    } catch {
      toast.error("Failed to load campaign");
      if (!isShared) navigate(-1);
    } finally { setLoading(false); }
  };

  const [shareUrl, setShareUrl] = useState("");

  const patchCampaign = async (body) => {
    try {
      const adminToken = localStorage.getItem("adminToken");
      if (!adminToken) { toast.error("Admin access required"); return; }
      await axios.patch(`${API}/campaigns/${campaignId}`, body, { headers: { Authorization: `Bearer ${adminToken}` } });
    } catch { toast.error("Save failed"); }
  };

  const handleExportPdf = useCallback(async () => {
    if (!reportRef.current) return;
    setExporting(true);
    try {
      const el = reportRef.current;
      // Temporarily expand to full width, remove max-width constraint for clean capture
      const origMaxW = el.style.maxWidth;
      const origPad = el.style.padding;
      el.style.maxWidth = "none";
      el.style.padding = "24px";

      const canvas = await html2canvas(el, {
        scale: 2,
        backgroundColor: theme.bg,
        useCORS: true,
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
        scrollX: 0,
        scrollY: 0,
      });

      el.style.maxWidth = origMaxW;
      el.style.padding = origPad;

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // A4 landscape dimensions in points
      const pdfPageW = 841.89;
      const pdfPageH = 595.28;
      const margin = 20;
      const usableW = pdfPageW - margin * 2;
      const scale = usableW / imgWidth;
      const scaledH = imgHeight * scale;
      const pageContentH = pdfPageH - margin * 2;

      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      let yOffset = 0;
      let page = 0;

      while (yOffset < scaledH) {
        if (page > 0) pdf.addPage();
        // Calculate source rectangle from canvas
        const srcY = (yOffset / scale);
        const srcH = Math.min(pageContentH / scale, imgHeight - srcY);
        // Create a temp canvas for this page slice
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = imgWidth;
        pageCanvas.height = Math.ceil(srcH);
        const ctx = pageCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, srcY, imgWidth, srcH, 0, 0, imgWidth, srcH);

        const pageImg = pageCanvas.toDataURL("image/jpeg", 0.92);
        pdf.addImage(pageImg, "JPEG", margin, margin, usableW, srcH * scale);
        yOffset += pageContentH;
        page++;
      }

      pdf.save(`${campaign.name || "Campaign Report"}.pdf`);
      toast.success("PDF exported");
    } catch (e) {
      console.error("PDF export error:", e);
      toast.error("Failed to export PDF");
    } finally { setExporting(false); }
  }, [campaign, theme.bg]);

  const handleUpdateField = async (field, value) => {
    const body = {};
    body[field] = field === "cpm" ? parseFloat(value) : value;
    await patchCampaign(body);
    toast.success("Updated");
    loadCampaign();
  };

  const handleHideSection = async (sectionId) => {
    const updated = [...hiddenSections, sectionId];
    setHiddenSections(updated);
    await patchCampaign({ hidden_sections: updated });
    toast.success("Section removed");
  };

  const handleGenerateInsights = async () => {
    setGeneratingInsights(true);
    try {
      const adminToken = localStorage.getItem("adminToken");
      const res = await axios.post(`${API}/campaigns/${campaignId}/generate-insights`, {}, { headers: { Authorization: `Bearer ${adminToken}` } });
      setCampaign(prev => ({ ...prev, insights: res.data.insights }));
      setInsightsText(res.data.insights);
      toast.success("AI insights generated");
    } catch { toast.error("Failed to generate insights"); }
    finally { setGeneratingInsights(false); }
  };

  const handleSaveInsights = async () => {
    await patchCampaign({ insights: insightsText });
    setCampaign(prev => ({ ...prev, insights: insightsText }));
    setEditingInsights(false);
    toast.success("Insights saved");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.bg }}><Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.accent }} /></div>;
  if (!campaign) return null;

  const s = campaign.summary || {};
  const cy = s.currency || "HKD";
  const hasVideo = campaign.has_video;
  const isHidden = (id) => hiddenSections.includes(id);
  const canEdit = isSuperAdmin && !isShared;

  const makeColumns = (rows, dimLabel) => {
    if (!rows?.length) return [];
    const dimKey = getDimKey(rows);
    const cols = [
      { key: dimKey, label: dimLabel, sortable: false, render: (v) => v },
      { key: "impressions", label: "Impressions", align: "right", render: v => fmtWhole(v) },
      { key: "clicks", label: "Clicks", align: "right", render: v => fmtWhole(v) },
      { key: "ctr", label: "CTR", align: "right", render: v => pct(v) },
      { key: "spend", label: `Spend (${cy})`, align: "right", render: v => fmtDec(v, 2) },
      { key: "cpc", label: `CPC (${cy})`, align: "right", render: v => fmtDec(v, 2) },
    ];
    if (rows.some(r => r.video_starts > 0)) {
      cols.push({ key: "video_starts", label: "Vid. Starts", align: "right", render: v => v ? fmtWhole(v) : "-" });
      cols.push({ key: "video_100", label: "Completions", align: "right", render: v => v ? fmtWhole(v) : "-" });
      cols.push({ key: "vtr", label: "VTR", align: "right", render: v => v ? pct(v) : "-" });
      cols.push({ key: "cpcv", label: "CPCV", align: "right", render: v => v ? fmtCpcv(v) : "-" });
    }
    return cols;
  };

  const breakdowns = [
    { key: "country_data", label: "Country" }, { key: "targeting_data", label: "Targeting" },
    { key: "format_data", label: "Ad Format" }, { key: "device_data", label: "Device" },
    { key: "audience_data", label: "Audience" }, { key: "ad_group_data", label: "Line Item" },
    { key: "creative_data", label: "Creative" },
  ].filter(b => campaign[b.key]?.length > 0);

  const hasDailyOrCountry = (campaign.daily_data?.length > 0) || (campaign.country_data?.length > 0);

  const ctrComparisonData = (campaign.country_data || []).map(r => ({
    name: getDimKey(campaign.country_data) ? r[getDimKey(campaign.country_data)] : "?", ctr: r.ctr || 0,
  })).sort((a, b) => b.ctr - a.ctr);
  const maxCtrVal = ctrComparisonData.length ? Math.max(...ctrComparisonData.map(d => d.ctr)) : 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.bg }}>
      {/* Header */}
      <header className="border-b" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!isShared && <button onClick={() => navigate(-1)} style={{ color: theme.textMuted }} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>}
            <img src={LOGO_URL} alt="Ingenium Digital" className="h-7" />
            <div className="h-6 w-px" style={{ backgroundColor: theme.border }} />
            <span className="text-xs" style={{ color: theme.textMuted }}>Campaign Report</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Theme toggle */}
            <button onClick={() => setThemeMode(m => m === "dark" ? "light" : "dark")} className="p-2 rounded-lg transition-colors" style={{ color: theme.textMuted, backgroundColor: `${theme.border}` }} data-testid="theme-toggle">
              {themeMode === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            {!isShared && (
              <>
                <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Share link copied"); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs transition-colors" style={{ borderColor: theme.border, color: theme.textMuted }} data-testid="share-btn"><Share2 className="w-3.5 h-3.5" /> Share</button>
                <button onClick={handleExportPdf} disabled={exporting} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-gradient-to-r from-[#ff3737] to-[#583aff] text-white text-xs hover:opacity-90 transition-opacity" data-testid="export-pdf-btn">
                  {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}{exporting ? "Exporting..." : "Export PDF"}
                </button>
              </>
            )}
            {isShared && (
              <button onClick={handleExportPdf} disabled={exporting} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-gradient-to-r from-[#ff3737] to-[#583aff] text-white text-xs hover:opacity-90 transition-opacity" data-testid="export-pdf-btn-shared">
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}{exporting ? "Exporting..." : "Export PDF"}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Report */}
      <div ref={reportRef} className="max-w-[1400px] mx-auto px-6 py-6 space-y-6">
        {/* Title + Meta */}
        <div>
          <h1 className="text-2xl font-bold" style={{ color: theme.text }} data-testid="campaign-title">
            {canEdit ? <EditableField value={campaign.name} onSave={v => handleUpdateField("name", v)} label="name" theme={theme} /> : campaign.name}
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-1">
            <span className="text-xs" style={{ color: theme.textFaint }}>CPM: {cy}{" "}{canEdit ? <EditableField value={s.cpm} onSave={v => handleUpdateField("cpm", v)} label="cpm" type="number" theme={theme} /> : s.cpm}</span>
            <span className="text-xs" style={{ color: theme.textFaint }}>Currency: {cy}</span>
            {(campaign.date_start || campaign.date_end) && (
              <span className="inline-flex items-center gap-1 text-xs" style={{ color: theme.textFaint }} data-testid="campaign-dates">
                <Calendar className="w-3 h-3" />
                {canEdit ? (
                  <>
                    <EditableField value={campaign.date_start || ""} onSave={v => handleUpdateField("date_start", v)} label="date-start" theme={theme} />
                    <span> — </span>
                    <EditableField value={campaign.date_end || ""} onSave={v => handleUpdateField("date_end", v)} label="date-end" theme={theme} />
                  </>
                ) : (
                  <span>{campaign.date_start} — {campaign.date_end}</span>
                )}
              </span>
            )}
            {campaign.created_at && <span className="text-xs" style={{ color: theme.textFaint }}>Uploaded: {new Date(campaign.created_at).toLocaleDateString()}</span>}
          </div>
        </div>

        {/* KPI Cards */}
        <Section id="kpi" hidden={isHidden("kpi")} onHide={handleHideSection} isShared={isShared} theme={theme}>
          <div className={`grid gap-3 ${hasVideo ? 'grid-cols-2 md:grid-cols-4 lg:grid-cols-7' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5'}`} data-testid="kpi-cards">
            <KpiCard icon={Eye} label="Impressions" value={fmtWhole(s.impressions)} theme={theme} />
            <KpiCard icon={MousePointer} label="Clicks" value={fmtWhole(s.clicks)} theme={theme} />
            <KpiCard icon={TrendingUp} label="CTR" value={pct(s.ctr)} theme={theme} />
            <KpiCard icon={DollarSign} label="CPC" value={cur(s.cpc, cy)} theme={theme} />
            <KpiCard icon={DollarSign} label="Total Spend" value={`${cy} ${fmtWhole(s.total_spend)}`} theme={theme} />
            {hasVideo && <KpiCard icon={PlayCircle} label="Video Completions" value={fmtWhole(s.video_100)} theme={theme} />}
            {hasVideo && <KpiCard icon={CheckCircle} label="Completion Rate" value={pct(s.completion_rate)} theme={theme} />}
          </div>
        </Section>

        {/* Daily Trends */}
        {campaign.daily_data?.length > 0 && (
          <Section id="daily" hidden={isHidden("daily")} onHide={handleHideSection} isShared={isShared} theme={theme}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-lg border p-5" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
                <h3 className="text-sm font-semibold mb-4" style={{ color: theme.text }}>Daily Impressions & Clicks</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={campaign.daily_data}>
                    <defs>
                      <linearGradient id="impGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ff3737" stopOpacity={0.3} /><stop offset="95%" stopColor="#ff3737" stopOpacity={0} /></linearGradient>
                      <linearGradient id="clkGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#583aff" stopOpacity={0.3} /><stop offset="95%" stopColor="#583aff" stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.gridStroke} />
                    <XAxis dataKey="date" tick={{ fill: theme.textMuted, fontSize: 10 }} tickFormatter={v => v?.slice(5)} />
                    <YAxis yAxisId="left" tick={{ fill: theme.textMuted, fontSize: 10 }} tickFormatter={v => fmtShort(v)} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: theme.textMuted, fontSize: 10 }} tickFormatter={v => fmtShort(v)} />
                    <Tooltip contentStyle={{ background: theme.tooltipBg, border: `1px solid ${theme.tooltipBorder}`, borderRadius: 8, fontSize: 11, color: theme.tooltipText }} labelStyle={{ color: theme.text }} />
                    <Area yAxisId="left" type="monotone" dataKey="impressions" stroke="#ff3737" fill="url(#impGrad)" strokeWidth={2} name="Impressions" />
                    <Area yAxisId="right" type="monotone" dataKey="clicks" stroke="#583aff" fill="url(#clkGrad)" strokeWidth={2} name="Clicks" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-lg border p-5" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
                <h3 className="text-sm font-semibold mb-4" style={{ color: theme.text }}>Daily CTR (%)</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={campaign.daily_data}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.gridStroke} />
                    <XAxis dataKey="date" tick={{ fill: theme.textMuted, fontSize: 10 }} tickFormatter={v => v?.slice(5)} />
                    <YAxis tick={{ fill: theme.textMuted, fontSize: 10 }} tickFormatter={v => v.toFixed(1) + "%"} />
                    <Tooltip
                      contentStyle={{ background: theme.tooltipBg, border: `1px solid ${theme.tooltipBorder}`, borderRadius: 8, fontSize: 11, color: theme.tooltipText }}
                      labelStyle={{ color: theme.text }}
                      formatter={v => [pct(v), "CTR"]}
                      cursor={{ fill: theme.themeMode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)' }}
                    />
                    <Bar dataKey="ctr" radius={[3, 3, 0, 0]} fill="#ff3737" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Section>
        )}

        {/* Overview Charts for reports without daily/country */}
        {!hasDailyOrCountry && breakdowns.length > 0 && (
          <Section id="overview-charts" hidden={isHidden("overview-charts")} onHide={handleHideSection} isShared={isShared} theme={theme}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <BreakdownChart data={campaign[breakdowns[0].key]} title={`Impressions by ${breakdowns[0].label}`} metric="impressions" metricLabel="Impressions" theme={theme} />
              <BreakdownChart data={campaign[breakdowns[0].key]} title={`CTR by ${breakdowns[0].label}`} metric="ctr" metricLabel="CTR" theme={theme} />
            </div>
          </Section>
        )}

        {/* Video Funnel */}
        {hasVideo && campaign.video_funnel && (
          <Section id="video-funnel" hidden={isHidden("video-funnel")} onHide={handleHideSection} isShared={isShared} theme={theme}>
            <VideoFunnel funnel={campaign.video_funnel} theme={theme} />
          </Section>
        )}

        {/* Country Breakdown */}
        {campaign.country_data?.length > 0 && (
          <Section id="country" hidden={isHidden("country")} onHide={handleHideSection} isShared={isShared} theme={theme}>
            <div className="space-y-4">
              <SortableTable title="Country Breakdown" data={campaign.country_data} columns={makeColumns(campaign.country_data, "Country")} greenMetric={["ctr", "cpc"]} campaignId={campaignId} section="country_data" token={token} theme={theme} />
              {ctrComparisonData.length > 1 && (
                <div className="rounded-lg border p-5" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
                  <h3 className="text-sm font-semibold mb-4" style={{ color: theme.text }}>CTR by Country</h3>
                  <div className="space-y-2">{ctrComparisonData.map(d => (
                    <div key={d.name} className="flex items-center gap-3">
                      <span className="text-xs w-28 truncate text-right" style={{ color: theme.textMuted }}>{d.name}</span>
                      <div className="flex-1 rounded-full h-4 overflow-hidden" style={{ backgroundColor: theme.border }}>
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${maxCtrVal > 0 ? (d.ctr / maxCtrVal * 100) : 0}%`, backgroundColor: theme.accent }} />
                      </div>
                      <span className="text-xs w-16 text-right font-semibold" style={{ color: theme.text }}>{pct(d.ctr)}</span>
                    </div>
                  ))}</div>
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Standard breakdowns */}
        {[
          { id: "targeting", key: "targeting_data", label: "Targeting Breakdown", dim: "Targeting" },
          { id: "format", key: "format_data", label: "Ad Format", dim: "Ad Size" },
          { id: "device", key: "device_data", label: "Device Breakdown", dim: "Device" },
          { id: "audience", key: "audience_data", label: "Audience Breakdown", dim: null },
          { id: "adgroup", key: "ad_group_data", label: "Line Item Breakdown", dim: "Line Item" },
          { id: "creative", key: "creative_data", label: "Creative Breakdown", dim: "Creative" },
        ].map(({ id, key, label, dim }) => campaign[key]?.length > 0 && (
          <Section key={id} id={id} hidden={isHidden(id)} onHide={handleHideSection} isShared={isShared} theme={theme}>
            <SortableTable title={label} data={campaign[key]} columns={makeColumns(campaign[key], dim || getDimKey(campaign[key]) || "Dimension")} greenMetric={["ctr", "cpc"]} campaignId={campaignId} section={key} token={token} theme={theme} />
          </Section>
        ))}

        {/* Other Breakdowns */}
        {campaign.other_breakdowns && Object.entries(campaign.other_breakdowns).map(([key, rows]) => (
          rows?.length > 0 && (
            <Section key={`other-${key}`} id={`other-${key}`} hidden={isHidden(`other-${key}`)} onHide={handleHideSection} isShared={isShared} theme={theme}>
              <SortableTable title={`${key} Breakdown`} data={rows} columns={makeColumns(rows, key)} greenMetric={["ctr", "cpc"]} campaignId={campaignId} section="other_breakdowns" token={token} theme={theme} />
            </Section>
          )
        ))}

        {/* Key Insights */}
        <Section id="insights" hidden={isHidden("insights")} onHide={handleHideSection} isShared={isShared} theme={theme}>
          <div className="rounded-lg border p-5" style={{ backgroundColor: theme.card, borderColor: theme.border }} data-testid="insights-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold" style={{ color: theme.text }}>Key Insights</h3>
              {canEdit && (
                <div className="flex items-center gap-2">
                  {editingInsights ? (
                    <>
                      <button onClick={handleSaveInsights} className="flex items-center gap-1 px-2 py-1 rounded bg-green-600/20 text-green-400 hover:bg-green-600/30 text-xs" data-testid="save-insights-btn"><Check className="w-3 h-3" /> Save</button>
                      <button onClick={() => { setEditingInsights(false); setInsightsText(campaign.insights || ""); }} className="flex items-center gap-1 px-2 py-1 rounded text-xs" style={{ color: theme.textFaint }}><X className="w-3 h-3" /> Cancel</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditingInsights(true)} className="flex items-center gap-1 px-2 py-1 rounded border text-xs" style={{ borderColor: theme.border, color: theme.textMuted }} data-testid="edit-insights-btn"><Pencil className="w-3 h-3" /> Edit</button>
                      <button onClick={handleGenerateInsights} disabled={generatingInsights} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-gradient-to-r from-[#ff3737] to-[#583aff] text-white text-xs hover:opacity-90 disabled:opacity-50" data-testid="generate-insights-btn">
                        {generatingInsights ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}{generatingInsights ? "Analyzing..." : "Generate AI Insights"}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {editingInsights ? (
              <textarea value={insightsText} onChange={e => setInsightsText(e.target.value)} className="w-full text-sm leading-relaxed p-3 rounded border outline-none resize-y min-h-[150px]" style={{ backgroundColor: theme.border, color: theme.textMuted, borderColor: `${theme.accent}30` }} data-testid="insights-textarea" />
            ) : campaign.insights ? (
              <div className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: theme.textMuted }}>{campaign.insights}</div>
            ) : (
              <p className="text-sm" style={{ color: theme.textFaint }}>{isShared ? "No insights available." : "Click 'Generate AI Insights' to analyze this campaign."}</p>
            )}
          </div>
        </Section>

        {/* Footer */}
        <div className="text-center py-4 border-t" style={{ borderColor: theme.border }}>
          <span className="text-xs" style={{ color: theme.textFaint }}>Ingenium Digital. All Rights Reserved. 2022-2026.</span>
        </div>
      </div>
    </div>
  );
}
